/* eslint-disable @typescript-eslint/dot-notation */
import { AuthenticationType, LoginWidgetContextData, OIDLoginWidgetExportData, LoginWidgetStateInfo, EaAuthenticateModule, EaAuthenticateComponent } from '@cudirect/ea-login';
import { Component, OnInit, OnDestroy, ViewChildren, QueryList, inject, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { LoginService } from 'src/user/service/login/login.service';
import { Subscription } from 'rxjs';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { LoginResponse } from 'src/user/model/login.response.model';
import { Result } from '@shared/model/result';
import { ConsumerUserService } from 'src/user/service/consumer-user.service/consumer-user.service';
import { TokenService, genTinyGUID, CookieService, JwtToken } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { LenderService } from '@shared/service/lender/lender.service';
import { ConsumerLogin } from 'src/user/model/consumer-login';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { ApiCacheService } from 'src/postapp/services/api.cache.service';
import { LoanType } from '@shared/enum';
import { RoutingUrl } from 'src/shared/constant/route-const';
import { UserSessionService } from '@shared/service/user-session.service';
import { Signup } from '@shared/interfaces/signup.api';
import { ConsumerRegistration } from 'src/user/model/consumer-registration';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { StatefulService } from '@app/service/stateful.service';
import { SubscriptionEmitService } from '@workflow/ecp-shared/service/subscription-emit/subscription-emit.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { GlobalService } from '@workflow/ecp-shared/service/global/global.service';
import { ElementClassName, Info, LocalStorageKeys, RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { FlowType } from '@workflow/ecp-shared/model/types';
import { StorageService } from '@workflow/service/storage/storage.service';
import {
  WorkflowPageNavigationStateService,
  NavigationPageState
} from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { WorkflowPageNavigationService } from '@workflow/service/workflow-page-navigation/workflow-page-navigation.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { XApiKeyService } from '@shared/service/x-api-key.service';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { IEconsentAcknowledgment, ILegalConsent } from '@workflow/ecp-workflow/dynamic-app/types/legal-consent.type';
import { ConsentComponent } from '../consent/consent.component';
import { FieldInstance } from '@workflow/class/field.service';
import { FlowInstance } from '@workflow/class/flow.service';
import { PageInstance } from '@workflow/class/page.service';
import { VerifyComponent } from '../verify/verify.component';
import { FormsModule } from '@angular/forms';

// Overview of the login component in 11 steps:
//
// 1. LoginComponent is responsible for handling various authentication
//    scenarios including login, heavy registration, light registration, and
//    verification.
//
// 2. Key Properties:
// - loginMode: Determines the current mode ('login', 'heavyReg', 'lightReg', or 'verify')
// - widgetContext: Holds the context data for the login widget
// - showSpinner: Controls the visibility of a loading spinner
// - showEconsent: Manages the display of eConsent content
//
// 3. Main Functionality:
// - ngOnInit(): The entry point of the component. It analyzes the URL,
//   handles cached tokens, and sets up the appropriate login mode.
// - setUpLoginMode(): Configures the component for standard login.
// - setupHeavyReg(): Prepares the component for a more detailed registration
//   process.
// - setUpVerifyFlow(): Sets up the verification flow for users.
//
// 4. Authentication Flow:
// - The component interacts with various services (LoginService,
//   TokenService, etc.) to manage authentication.
// - It handles different authentication types and can process query
//   parameters for things like auth codes.
//
// 5. User Session Management:
// - Works with UserSessionService to manage user sessions.
// - Handles cached tokens and can navigate users to the dashboard if already
//   authenticated.
//
// 6. Registration Handling:
// - Can handle both light and heavy registration processes.
// - Manages eConsent for registration when necessary.
//
// 7. Verification:
// - Supports user verification, potentially using methods like phone or email.
//
// 8. Integration with Other Services:
// - Interacts with multiple services for tasks like API caching, bundle
//   management, and application insights logging.
//
// 9. Routing:
// - Works with Angular's Router for navigation between different parts of
//   the application.
//
// 10. State Management:
// - Uses various services to manage application state, including
//   StatefulService and WorkflowPageNavigationStateService.
//
// 11. Error Handling:
// - Has mechanisms to display alert messages and handle various error
//   scenarios.
//
// Now Here's a simplified representation of the login component's flow using
// Mermaid:
//
// ```mermaid
// graph TD
//     A[Login Component] --> B{Login Mode}
//     B -->|Login| C[Setup Login Mode]
//     B -->|Heavy Reg| D[Setup Heavy Reg]
//     B -->|Verify| E[Setup Verify Flow]

//     C --> F[Get Widget Context]
//     C --> G[Handle Cached Token]
//     C --> H[Process Query Params]

//     D --> I[Get Registration Data]
//     D --> J[Handle eConsent]

//     E --> K[Verify User]

//     F --> L[Initialize Token Service]
//     F --> M[Set Authentication Type]

//     N[Received Data Handler] --> O{Login Mode}
//     O -->|Login/Verify| P[Handle Login Data]
//     O -->|Heavy Reg| Q[Handle Heavy Reg Data]

//     P --> R[Get Login Data]
//     P --> S[Logout if No Token]

//     Q --> T[Set Login Data]
//     Q --> U[Update eConsent]

//     V[After Login] --> W[Set OAO Member]
//     V --> X[Assign Bundle]
//     V --> Y[Navigate to Dashboard/Overview]
// ```
//
// - Login component handles different modes: login, heavy-reg, and verify
// - Each mode has specific setup and data handling processes
// - Authentication and token management are key across all modes
// - eConsent and registration data are important for heavy reg
// - After login, user data is set and navigation
//
// Now let's see a simplified representation of the login flow using Mermaid:
//
// ```mermaid
// graph TD
//     title[(<u>Login Flow</u>)]
//     A[ngOnInit] --> B[analyzeURL]
//     A --> C[handleCachedToken]
//     C -->|true| D[Navigate to Dashboard/Overview]
//     C -->|false| E{Switch loginMode}

//     E -->|login| F[setUpLoginMode]
//     E -->|heavyReg| G[setupHeavyReg]
//     E -->|verify| H[setUpVerifyFlow]

//     A --> I[tokenService.getCachedToken]

//     F --> J[getWidgetContext]
//     F --> K[processQueryParams]

//     K --> L{Check authCode}
//     L -->|exists| M[getLoginData]
//     L -->|doesn't exist| N{Check if not from SSO}
//     N -->|true| O[Set showSpinner to false]

//     A --> P[userSession.stopTimer]

//     subgraph Legend
//         R[Component Method]
//         S[Service Method]
//         T{Decision}
//     end
// ```
//
// 1. `ngOnInit` is the entry point.
// 2. It calls `analyzeURL` to determine the login mode.
// 3. It then calls `handleCachedToken`.
// 4. Depending on the login mode, it calls one of `setUpLoginMode`,
//    `setupHeavyReg`, or `setUpVerifyFlow`.
// 5. `setUpLoginMode` calls `getWidgetContext` and `processQueryParams`.
// 6. `processQueryParams` checks for an auth code and potentially calls `getLoginData`.
interface SlideOutContent {
  title: string;
  header: string;
  description: string;
}
type ModeType = 'login' | 'heavyReg' | 'lightReg' | 'verify';

@Component({
  selector: 'cu-login-master',
  templateUrl: './login.component.html',
  standalone: true,
  imports: [RouterModule, ConsentComponent, EaAuthenticateModule, VerifyComponent, FormsModule, EaAuthenticateComponent]
})
export class LoginComponent implements OnInit, OnDestroy {
  // Using inject pattern for dependencies
  private readonly logger = inject(SawDust);
  private readonly loginService = inject(LoginService);
  private readonly router = inject(Router);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly consumerUserService = inject(ConsumerUserService);
  private readonly tokenService = inject(TokenService);
  private readonly subscribeEmitService = inject(SubscriptionEmitService);
  private readonly lenderService = inject(LenderService);
  public readonly userService = inject(UserService);
  private readonly storageService = inject(StorageService);
  private readonly apiCacheService = inject(ApiCacheService);
  private readonly bundleManagerService = inject(BundleHelperService);
  private readonly pageNavigationStateService = inject(WorkflowPageNavigationStateService);
  private readonly l360Service = inject(L360Service);
  private readonly userSession = inject(UserSessionService);
  public readonly dynamicPageService = inject(WorkflowPageNavigationService);
  private readonly workflowService = inject(WorkflowService);
  private readonly applicationApiService = inject(ApplicationApiService);
  private readonly bundleHelperService = inject(BundleHelperService);
  private readonly documentRef = inject(DOCUMENT);
  private readonly cookieService = inject(CookieService);
  private readonly statefulService = inject(StatefulService);
  private readonly adminPortalFeaturesService = inject(AdminPortalFeaturesService);
  private readonly globalService = inject(GlobalService);
  private readonly xApiKeyService = inject(XApiKeyService);
  private readonly googleTrackingService = inject(GoogleTrackingService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  public loginMode: ModeType;
  public widgetContext: LoginWidgetContextData;
  public lenderConfig: LenderConfiguration;

  public showWidget = false;
  private subscriptions = new Subscription();
  /** holds error message */
  public alertMessage = '';

  /** flag to show/hide spinner */
  public showSpinner = true;

  /** flag to show/hide eConsent */
  public primaryAck: IEconsentAcknowledgment | null;
  public additionalAck: IEconsentAcknowledgment[];
  public consentLibrary: ILegalConsent[];
  @ViewChildren(ConsentComponent)
  public consents: QueryList<ConsentComponent>;

  public consentField: FieldInstance = { automationId: 'econsent' } as FieldInstance;
  /** holding value to determine if consents are accepted and in a valid state */
  public consentsValid = true;
  public showEconsent = false;
  public econsentContent: SlideOutContent;

  /** loanNumber */
  public loanNumber: string;

  /** borrowerId */
  public borrowerId = '';

  /** used for the logging event */
  private eventPairId: string;

  /** used for the session get parameters */
  public widgetParam: { autoredirect?: boolean; oidcid?: string; forgetPw?: boolean; econsent?: boolean; authType?: string } = {};
  public isEnable = false;

  /** registration Form */
  public registrationForm = {} as Signup;

  // ** Heavy reg specific
  /** Return auto loan flag */
  public isAutoLoan = true;

  /** loginURL */
  public loginURL = './user/login';

  /** user object */
  public user = {} as ConsumerRegistration;

  public get isOlaRegistration(): boolean {
    return this.userService?.flowType?.toLowerCase() === LoanType.OLA.toLowerCase();
  }
  /** Storage Prefix */
  public storagePrefix = 'api';

  /** applicationStage Url */
  public applicationStage = `ola:loan/application/loanApplications/$/applicationStage`;
  /** Redirection path after light registration */
  public redirectPath: string;

  // Tracks whether we are in the oid heavy reg flow for app insights purposes.
  public oidHeavyReg = false;
  public verifyMethod?: 'Email_Code' | 'Phone';
  public userId = '';
  public urlForMode: string;

  constructor() {
    this.userSession.logoutInProgress = false;
  }

  /**
   * Map the URL to a specific mode for login type.
   * @param url - string
   * @returns - ModeType
   */
  private analyzeURL(url: string): ModeType {
    // Strip any query params without changing url.
    const urlSplit = url.split('?');
    url = urlSplit[0];

    // Default to login mode.
    let mode = 'login' as ModeType;

    switch (url) {
      case '/user/login':
        mode = 'login' as ModeType;
        break;
      case '/user/registration':
        mode = 'heavyReg' as ModeType;
        break;
      case '/user/verify':
        mode = 'verify' as ModeType;
        break;
    }
    return mode;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Check: Currently, in arcDX, if any subsequent calls fail token validation, the user is dropped back
  // at login with no messaging. There should be a query param used to display an 'Access Denied' alert of some sort.

  public ngOnInit(): void {
    this.urlForMode = this.router.url; // Temp fix for ng15 handling routing differently than ng14
    this.userSession.stopTimer();
    this.loginMode = this.analyzeURL(this.urlForMode);

    const cachedToken = this.tokenService.getCachedToken() || undefined;
    // this.logger.info('In onInit. Checking loginmode things.', this.loginMode, cachedToken);
    if (this.loginMode !== 'heavyReg' && this.handleCachedToken(cachedToken)) {
      return;
    }

    const preventCachedLogin = Object.keys(this.activeRoute.snapshot.queryParams).length > 0;
    if (cachedToken && !preventCachedLogin && this.loginMode !== 'login' && this.loginMode !== 'heavyReg') {
      this.receivedDataHandler({ token: cachedToken, statusMsg: 'loggedIn' });
    }

    // Look at the login mode to decide how to set up.
    switch (this.loginMode) {
      case 'login':
        this.setUpLoginMode();
        break;
      case 'heavyReg':
        this.setupHeavyReg();
        break;
      case 'verify':
        this.setUpVerifyFlow();
        break;
      default:
        this.setUpLoginMode();
    }
  }

  private handleCachedToken(cachedToken?: JwtToken): boolean {
    if (cachedToken && this.tokenService.determineTokenType() === 'arcDX') {
      this.router.navigate([this.loginMode === 'verify' ? RoutingUrl.OAO.Overview : RoutingUrl.Dashboard]);
      return true;
    }
    return false;
  }

  /**
   * Contains the converted ngInit from the old login component.
   */
  private setUpLoginMode(): void {
    const loginData = this.loginService.getLoginData();
    if (loginData) {
      this.receivedDataHandler(loginData);
      this.loginService.clearLoginData();
      return;
    }
    this.showSpinner = true;
    this.eventPairId = genTinyGUID(24); // Check: Replace this with cypto.randomUUID() when TS is updated
    const params = this.activeRoute.snapshot.queryParams;
    if (this.isBrowser) {
      const body = this.documentRef?.getElementsByTagName(Info.body)[0];
      body?.classList.remove(ElementClassName.bodyFooter);
    }
    if (params.authType) {
      this.widgetParam.authType = params.authType;
    }

    if (params.authCode !== undefined) {
      const authCode = params.authCode;
      this.getLoginData(undefined, undefined, undefined, undefined, undefined, undefined, undefined, authCode);
    } else {
      if (!this.isRequestFromSSOPage()) {
        this.showSpinner = false;
      }
    }
    this.widgetParam.autoredirect = this.getFromSession('autoredirect')?.toLowerCase() === 'true';
    this.widgetParam.oidcid = this.getFromSession('oidcid');
    this.widgetParam.forgetPw = params.resetPassword === 'true';
    if (this.isBrowser) {
      sessionStorage.removeItem('autoredirect');
    }

    this.getWidgetDataForLogin();

    this.subscriptions.add(
      this.activeRoute.queryParams.subscribe((qParams) => {
        if (qParams?.inactive) {
          this.loginService.setLoginAlert({
            type: 'warning',
            heading: 'Session Timeout',
            message: 'Your session has expired due to inactivity. Please log in again.'
          });
          // Clear the query params

          if (this.isBrowser) {
            const newURL = window.location.href.split('?')[0];
            window.history.pushState('object', this.documentRef?.title ?? '', newURL);
          }
        }
      })
    );
    this.showWidget = true;
  }

  private isRequestFromSSOPage(): boolean {
    const isSSOLogin =
      (this.activeRoute.snapshot.queryParams &&
        this.activeRoute.snapshot.queryParams.state &&
        this.activeRoute.snapshot.queryParams.autoredirect &&
        !this.activeRoute.snapshot.queryParams.error) ||
      this.widgetParam.autoredirect;
    return isSSOLogin;
  }

  private getFromSession(key: string): string {
    if (!this.isBrowser) {
      return undefined;
    }
    const value = sessionStorage.getItem(key);
    return value !== null ? value : undefined;
  }

  private getWidgetDataForLogin(): void {
    // Set Lender Data (API)
    this.lenderConfig = this.lenderService.lenderDataInfo;

    // Need to translate widgetParams before grabbing context.
    const extra = {
      oidcAutoRedirect: false,
      config: {
        optionalOidcRedirectId: null
      },
      deepLink: null
    };
    if (this.widgetParam.autoredirect) {
      extra.oidcAutoRedirect = true;
    } else {
      delete extra.oidcAutoRedirect;
    }

    if (this.widgetParam.oidcid) {
      extra.config.optionalOidcRedirectId = this.widgetParam.oidcid;
    } else {
      delete extra.config;
    }

    if (this.widgetParam.forgetPw) {
      extra.deepLink = 'reset';
    } else {
      delete extra.deepLink;
    }

    if (this.widgetParam.authType) {
      extra['authenticationType'] = this.widgetParam.authType;
    }

    this.loginService.getWidgetContext('login', extra).then((res) => {
      this.widgetContext = res;
      if (this.isBrowser) {
        this.tokenService.initialize(
          this.widgetContext.oid.apiBaseURL,
          this.xApiKeyService.createApiHeader(),
          this.widgetContext.oid.oidConfigName,
          true
        );
      }

      // Check if auth type cookie has been set
      const authTypeCookie = this.cookieService.getCookie('arcDXAuthType');
      if (authTypeCookie && !this.widgetContext.authenticationType) {
        this.widgetContext.authenticationType = authTypeCookie as AuthenticationType;
      }

      this.isEnable = !this.isRequestFromSSOPage();
      if (this.activeRoute.snapshot.queryParams.state && !this.activeRoute.snapshot.queryParams.autoredirect) {
        this.isEnable = false;
      }

      const loginAlert = this.loginService.getLoginAlert();
      if (loginAlert) {
        this.widgetContext.setAlert = loginAlert;
        this.widgetContext = { ...this.widgetContext };
        this.loginService.clearLoginAlert();
      }
    });
  }

  /**
   * Centralized logging method to avoid duplicate calls
   * @param eventName - Name of the event to log
   * @param params - Parameters to include in the log
   */
  private logEvent(eventName: string, params): void {
    this.logger.logEvent(eventName, params);
  }
  /**
   * Process after login complete.
   * @param response data body
   * @param aILog specifies if there is logging to Application Insights, default to true
   */
  private afterLogin(response: Result<LoginResponse>, aILog = true): void {
    if (aILog) {
      this.logEvent('ecp.ui.login', { eventState: 'end', correlationId: this.eventPairId });
    }

    if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
      this.bundleManagerService.refreshBundle();
      this.userService.user.firstName = response.data?.user?.firstName;
      this.userService.user.lastName = response.data?.user?.lastName;
      this.setOAOMember(response.data.applicationBundle);
      /** Updating OAO related settings which comes from L360 */
      this.l360Service.setOAOLenderSettings();
    }
    this.assignBundle(() => {
      if (this.loginMode === 'verify') {
        this.router.navigate([RoutingUrl.OAO.Overview]);
      } else {
        this.router.navigate([RoutingUrl.Dashboard]);
      }
    });
  }

  private processErrorMessage(): string {
    const alertMessage = Info.loginErrorMessage;
    this.logEvent(this.oidHeavyReg ? 'ecp.ui.heavy-reg' : 'ecp.ui.login', {
      eventState: 'end',
      correlationId: this.eventPairId,
      status: 'error',
      message: alertMessage
    });

    return alertMessage;
  }

  public setOAOMember(user): void {
    if (user) {
      const userExtraData = user.extraData;
      this.statefulService.wrap('api:extraData').value = userExtraData;
      this.pageNavigationStateService.setStoragePrefix('oao');
      this.pageNavigationStateService.retrievePageState();
      if (userExtraData) {
        this.setNavbarStatus(userExtraData);
      }
    }
  }

  private assignBundle(cb?: () => void): void {
    cb();
  }

  /**
   * Handles the state emissions from the login widget.
   * @param data the LoginWidgetStateInfo Object
   */
  public receivedStateInfoHandler(data: LoginWidgetStateInfo | string) {
    data = data as LoginWidgetStateInfo;
    if (this.loginMode === 'heavyReg') {
      if (data.event === 'newUser' && data.code === 'creationDeffered') {
        this.logEvent('ecp.ui.heavy-reg', { eventState: 'start', correlationId: this.eventPairId });
        this.oidHeavyReg = true;
        this.getRegistrationData(data);
      } else if (data.event === 'eConsent' && data.code === 'acceptance') {
        if (!data.success) {
          // This means they previously clicked agree and then click again to disagree.
          this.setAlert('warning', 'You need to accept eConsent in order to continue.');
        }
      } else if (data.event === 'eConsent' && data.code === 'show') {
        // Show the eConsent slide out panel
        this.showEconsent = true;
      } else if (data.event === 'timeout' && data.code === 'sendToLogin') {
        this.router.navigate(['/']);
      }
    }
  }

  /**
   * Handles the completion data from the login widget.
   * @param data the LoginWidgetExportData
   */
  public receivedDataHandler(data: OIDLoginWidgetExportData | string): void {
    data = data as OIDLoginWidgetExportData;
    this.isEnable = false;

    if (this.loginMode === 'login' || this.loginMode === 'verify') {
      this.handleReceivedDataForLoginMode(data);
    } else if (this.loginMode === 'heavyReg') {
      this.handleReceivedDataForHeavyReg(data);
    } else {
      this.logger.error('Something went wrong. No login mode matched.', { component: 'LoginComponent' });
    }
  }

  private handleReceivedDataForLoginMode(data: OIDLoginWidgetExportData): void {
    this.isEnable = false;

    if (!data.token) {
      this.logout();
    } else if (typeof data.token === 'object' && Object.keys(data.token).length > 0 && data.token.access_token) {
      this.googleTrackingService.publishEvent('arcdx.login-verify-success', {});

      // Need to get email from encoded token.
      const decodedToken = this.tokenService.decodeToken(data.token.access_token);
      const email = decodedToken.email;
      const phoneNumber = decodedToken.phonenumber;

      // hack until api is normalized
      this.getLoginData(
        email,
        phoneNumber,
        data.token.access_token,
        data.token.refresh_token,
        false,
        undefined,
        undefined,
        undefined,
        data.token.id_token,
        undefined
      );
    }
  }
  /**
   * Handling Heavy Reg flow
   * @param data - Login Data
   */
  private handleReceivedDataForHeavyReg(data: OIDLoginWidgetExportData): void {
    // This will possibly be refactored in the future. Setting this variable in case the user had a previous session and logged
    // out to do heavy reg. This will make a new bundle be created below.
    if (this.isBrowser) {
      sessionStorage.setItem('arcDXHeavyReg', 'redirecting');
    }
    if (data.token) {
      this.loginService.setLoginData(data);
      this.updateEconsent();
    }

    // If we get this far, end the event. TODO: This might never fire. Investigate.
    if (this.oidHeavyReg) {
      this.logEvent('ecp.ui.heavy-reg', {
        eventState: 'end',
        correlationId: this.eventPairId
      });
      this.oidHeavyReg = false;
    }
    // If the widget completed, navigate the user to login. If there was a token, it will use it to complete auto login.
    // If there's no token, login failed and the user can try again.

    this.router.navigate([RoutingUrl.Login]);
  }

  private logout(): void {
    if (this.userService.user) {
      this.userService.logout();
      this.apiCacheService.clear();
      this.storageService.clear();
    }
  }

  /**
   * Get the log in data?
   * @param email
   * @param accessToken
   * @param refreshToken
   * @param ssoLogin
   * @param idToken
   * @param expiresIn
   * @param authCode
   * @param extToken
   * @param discoveryURL
   */
  public getLoginData(
    email = '',
    phoneNumber = '',
    accessToken = '',
    refreshToken = '',
    ssoLogin = false,
    idToken = '',
    expiresIn = '',
    authCode = '',
    extToken = '',
    discoveryURL = ''
  ): void {
    this.logEvent('ecp.ui.login', { eventState: 'start', correlationId: this.eventPairId });

    const user = {} as ConsumerLogin;

    if (authCode && authCode !== '') {
      user.authCode = authCode;
    }

    // Widget Login Flow
    else {
      user.email = email;
      user.accessToken = accessToken;
      user.refreshToken = refreshToken;
      user.loginWithSSO = ssoLogin;
      user.idToken = idToken;
      user.expiresIn = expiresIn;
      user.hbAccessToken = extToken;
      user.discoveryURL = discoveryURL;
      user.cellPhone = phoneNumber;
    }

    const oldSession = this.statefulService.wrap(`${this.isOlaFlow ? 'ss:auth-ola' : 'ss:authorization'}`).valueString;
    // This has nothing to do with a mortgage login, and there is no where in the app ss version of this is set to true
    const isMortgageLogin = this.statefulService.wrap('ss:loginType').value;
    // Not sure why we are forcing a logout during a potential login flow
    if (this.loginMode !== 'verify') this.logout();
    let prevSession = '';
    if (this.loginMode === 'verify') {
      prevSession = oldSession ?? null;
    }
    // Not sure when we need to clear out the storageService, but we definitely should not do it during the verify step
    // TODO: We should also check this block when users are autologged in via HB and Instatouch
    if (isMortgageLogin !== true && isMortgageLogin !== 'true' && this.loginMode !== 'verify') {
      // Clearing this here will also make isAllBackendInitialized return false, 
      // which will force re-initialization of backends and create new bundles
      this.storageService.clear();
    }
    this.statefulService.wrap('ss:loginType').value = false;
    // We dont use heavy reg anymore, so this may not be needed, but some lenders are still using it.
    const comingFromHeavyReg = this.isBrowser ? sessionStorage.getItem('arcDXHeavyReg') : null;

    if (oldSession && !comingFromHeavyReg && this.storageService.isAllBackendInitialized()) {
      this.getLoginDataContinued(oldSession, user, prevSession);
    } else {
      // This create new bundles
      this.storageService.initAllBackends().finally(() => {
        this.getLoginDataContinued(oldSession, user, prevSession);
      });
    }
  }

  // This method was previously code inside the finally block above. This should only create bundles if there isn't an existing
  // session, so moved to its own method.
  public getLoginDataContinued(oldSession, user, prevSession) {
    // Removing the flag that was set in the heavy reg flow. This will fail silenly if it doesn't exist.
    if (this.isBrowser) {
      sessionStorage.removeItem('arcDXHeavyReg');
    }
    if (this.userSession.canHookCurrentBundle && oldSession) {
      this.userService.user = new UserInfo();
      this.userService.user.token = oldSession;
    }
    const loginSub = this.consumerUserService.login(user, prevSession).subscribe(
      (response: Result<LoginResponse>): void => {
        this.userSession.canHookCurrentBundle = false;
        if (response?.isSuccess && response.data) {
          this.SetUser(response.data);
          const loanLinkInfo = this.GetLoanLinkInfo(user);
          if (!loanLinkInfo.loanNumber || !loanLinkInfo.borrowerId) {
            this.afterLogin(response);
            return;
          }
          this.consumerUserService.LinkLoan(loanLinkInfo).subscribe(
            (): void => {
              this.afterLogin(response);
            },
            () => {
              this.showSpinner = false;
              this.widgetContext.setAlert = {
                type: 'error',
                heading: '',
                message: this.processErrorMessage()
              };
              this.widgetContext = { ...this.widgetContext };
              this.afterLogin(response, false);
            }
          );
        }
      },
      () => {
        this.showSpinner = false;
        setTimeout(() => {
          this.widgetContext.setAlert = {
            type: 'error',
            heading: '',
            message: this.processErrorMessage()
          };
          this.widgetContext = { ...this.widgetContext };
          this.isEnable = true;
        }, 100);
      }
    );
    this.subscriptions.add(loginSub);
  }

  /** Set right side nav bar status after user login */
  // Dont think we need this any longer
  private setNavbarStatus(extraData: string): void {
    const objPageStateJSON = JSON.parse(extraData) as NavigationPageState;
    const completedPages = this.flow?.pages?.filter(
      (t) => t.visible === true && t.helper.isPageComplete === true && t.helper.invalidFields.length === 0
    );
    if (completedPages && completedPages.length > 0) {
      completedPages.forEach((page: PageInstance) => {
        if (objPageStateJSON[page.id]?.isVisited && objPageStateJSON[page.id]?.state.indexOf('completed') >= 0) {
          this.dynamicPageService.updateNavState(page, true);
        }
      });
    }
  }

  /**
   * Get Loan Link Info
   */
  public GetLoanLinkInfo(user: ConsumerLogin): ConsumerLogin {
    const loanLinkInfo = {} as ConsumerLogin;
    if (this.activeRoute.snapshot.queryParams.borrowerId !== undefined && this.activeRoute.snapshot.queryParams.loanNumber !== undefined) {
      this.borrowerId = this.activeRoute.snapshot.queryParams.borrowerId;
      this.loanNumber = this.activeRoute.snapshot.queryParams.loanNumber;
      this.showSpinner = false;
      loanLinkInfo.email = user.email;
      loanLinkInfo.borrowerId = this.borrowerId;
      loanLinkInfo.loanNumber = this.loanNumber;
    }
    return loanLinkInfo;
  }

  /** Flow instance */
  public get flow(): FlowInstance {
    return this.workflowService.currentFlow;
  }

  /**
   * SetUser
   */
  public SetUser(response: LoginResponse): void {
    const userInfo = Object.assign(new UserInfo(), response.applicationBundle);
    userInfo.email = response.user.email;
    userInfo.cellPhone = response.user.cellPhone;
    userInfo.isCellPhoneUser = response.user.isCellPhoneUser;
    userInfo.token = response.credentials.access_token;
    userInfo.consumer_id = response.applicationBundle.consumerId;
    userInfo.token_time = new Date();
    userInfo.firstName = response.user.firstName || response.applicationBundle.firstName;
    userInfo.lastName = response.user.lastName || response.applicationBundle.lastName;
    userInfo.userName = `${userInfo.firstName} ${userInfo.lastName}`;
    userInfo.confetti = [];
    userInfo.registeredWithSSO = response.applicationBundle.registeredWithSSO;
    userInfo.consentAcceptences = response.user['consentAcceptences'] ?? [];

    if (
      response.applicationBundle.loans &&
      response.applicationBundle.loans.length > 0 &&
      response.applicationBundle.loans[0].loanNumber !== null
    ) {
      userInfo.loanNumber = response.applicationBundle.loans[0].loanNumber.trim();
      this.loanNumber = userInfo.loanNumber;
    }

    this.userService.user = userInfo;
    if (response.applicationBundle?.loginType?.toLowerCase() === LoanType.OLA.toLowerCase()) {
      this.userService.flowType = LoanType.OLA;
    }

    this.userSession.startTimer();

    this.statefulService.wrap('ss:authorization').value = response.credentials.access_token;
    // this.statefulService.wrap('ss:auth-ola').value = response.credentials.access_token;
    setTimeout(() => {
      this.subscribeEmitService.emit<boolean>('BorrowerFullname', true);
    }, 100);
  }

  public get flowType(): FlowType {
    return this.userService.flowType ? this.userService.flowType : LoanType.Mortgage;
  }
  /** Hold the value to Show and hide OAO Flow texts. */
  public get isOaoFlow() {
    return this.flowType.toLowerCase() === LoanType.OAO.toLowerCase();
  }
  /** Hold the value to Show and hide OLA Flow texts. */
  public get isOlaFlow() {
    return this.flowType.toLowerCase().toLowerCase() === LoanType.OLA.toLowerCase();
  }

  /**
   * Sets up the verify flow for login widget
   */
  private setUpVerifyFlow(): void {
    const userInfo = this.userService.user;
    const method: 'Email_Code' | 'Phone' | undefined = userInfo?.mfaVerifyMethod;

    if (!userInfo || !method) {
      this.userId = '';
      this.verifyMethod = undefined;
      return;
    }

    this.verifyMethod = method;
    this.userId = method === 'Phone' ? userInfo.cellPhone : userInfo.email;
  }

  /**
   * Sets an alert for the login widget.
   * @param type type of the alert
   * @param message message of the alert
   * @param heading heading of the alert
   */
  public setAlert(type: 'warning' | 'error' | 'info' | 'success', message: string, heading = ''): void {
    this.widgetContext.setAlert = {
      type,
      heading,
      message
    };
    this.widgetContext.preventAlertRedirect = true;
    this.widgetContext = { ...this.widgetContext };
  }

  /**
   * Do we still need this eConsent? Can we reomve
   * SSO: Update econsent bundle for ola or oao
   */
  private updateEconsent(callApi = false): void {
    const storagePrefix = this.isOlaFlow ? 'ola' : 'api';
    this.statefulService.wrap(`${storagePrefix}:consent/consentType`).value = 'eConsent';
    this.statefulService.wrap(`${storagePrefix}:consent/isAccepted`).value = true;
    this.statefulService.wrap(`${storagePrefix}:consent/agreedDate`).value = new Date().toISOString();
    this.statefulService.wrap(`${storagePrefix}:consent/declinedDate`).value = null;
    this.statefulService.wrap(`${storagePrefix}:consent/isDeclined`).value = false;
    // **** Testing adding lines from the econsent component.
    if (callApi) {
      const rawReqBody = [
        {
          op: 'add',
          path: `consent/agreedDate`,
          value: new Date().toISOString()
        },
        {
          op: 'add',
          path: `consent/consentType`,
          value: 'eConsent'
        },
        {
          op: 'add',
          path: `consent/isAccepted`,
          value: true
        },
        {
          op: 'add',
          path: `consent/declinedDate`,
          value: null
        },
        {
          op: 'add',
          path: `consent/isDeclined`,
          value: false
        }
      ];

      const isOlaRegistration = this.userService?.flowType?.toLowerCase() === LoanType.OLA.toLowerCase();

      const bundleSub = this.applicationApiService.bundle(rawReqBody, this.flowType).subscribe(
        () => {
          if (isOlaRegistration) {
            this.bundleHelperService.refreshOLABundle();
          } else {
            this.bundleHelperService.saveBundle();
          }
        },
        () => {}
      );
      this.subscriptions.add(bundleSub);
    }
  }

  // ******************** Heavy Reg ****
  /**
   * Looks like users can still hit this route even when logged in. Might need to route guard and or
   * add messaging here to let them know they are already logged in.
   * Contains the ngOnInit from the heavy reg component, plus some specific widget context handling.
   */
  public setupHeavyReg(): void {
    // If there is a logged in user in session, log them out and clear the session.
    const token = this.tokenService.getCachedToken();
    // this.logger.info('About to log user out');
    // this.logger.info('Token - ', tempToken);
    // this.logger.info('User service user', this.userService.user);
    if (token) {
      this.userSession.logOut(false);
    }

    this.eventPairId = genTinyGUID(24);
    if (this.isBrowser) {
      sessionStorage.setItem('registration', 'registration');
    }
    // From the getWidgetData Method - This method was only called from the constructor, so putting here before the promise resolves.
    // Set Lender Data (API)

    const extraParam = {
      // eslint-disable-next-line eqeqeq
      oidcAutoRedirect: this.getFromSession('autoredirect')?.toLowerCase() == 'true',
      config: {
        optionalOidcRedirectId: this.getFromSession('oidcid')
      }
    };
    if (this.isBrowser) {
      sessionStorage.removeItem('autoredirect');
    }
    this.lenderConfig = this.lenderService.lenderDataInfo;

    if (this.isRequestFromSSOPage()) {
      this.showSpinner = true;
    } else {
      this.showSpinner = false;
      this.isEnable = true;
    }

    // ************ end getWidgetData

    this.loginService.getWidgetContext('heavyReg', extraParam).then((res) => {
      // Add eConsent verbiage to the widget context.
      res = this.addEConsentText(res);
      this.widgetContext = res;
      if (this.isBrowser) {
        this.tokenService.initialize(
          this.widgetContext.oid.apiBaseURL,
          this.xApiKeyService.createApiHeader(),
          this.widgetContext.oid.oidConfigName,
          true
        );
      }
      this.showSpinner = !this.isAutoLoan;
      // ** from init
      if (
        this.activeRoute.snapshot.queryParams.BorrowerID !== undefined &&
        this.activeRoute.snapshot.queryParams.LoanNumber !== undefined
      ) {
        this.borrowerId = this.activeRoute.snapshot.queryParams.BorrowerID;
        this.loanNumber = this.activeRoute.snapshot.queryParams.LoanNumber;
        this.loginURL += `?loanNumber=${this.loanNumber}&borrowerId=${this.borrowerId}`;
      }

      const loginPageURL = this.loginURL;
      if (this.router?.url?.includes('?')) {
        const splitUrl = this.router.url.split('?');
        if (splitUrl && splitUrl.length > 0) {
          this.loginURL = loginPageURL + (loginPageURL.includes('?') ? '&' : '?') + splitUrl[1];
        }
      }

      this.clearUserSession();
      this.showWidget = true;
    });
  }

  // TODO: This user service lives in WW. That needs to be refactored.
  public clearUserSession(): void {
    this.userService.removeFromLocalStorage(LocalStorageKeys.userStorageName);
  }

  // Called from the Heavy Reg widget state handler. Converts the state info into a user object, and if isAutoLoan, creates an account
  public getRegistrationData(response: LoginWidgetStateInfo): void {
    this.user.firstName = response.data.firstName;
    this.user.lastName = response.data.lastName;
    this.user.last4Ssn = response.data.ssn.toString();
    this.user.email = response.data.email;
    this.user.dateOfBirth = response.data.dob;
    this.user.loanNumber = this.loanNumber;
    this.user.isHeavyRegistration = this.oidHeavyReg;
    if (this.isAutoLoan) {
      this.user.loginType = LoanType.OLA;
      this.createAccount();
    }
  }

  public createAccount(): void {
    const extraParam = {
      createNewFlow: {
        deferredCreationState: 'complete'
      }
    };
    this.consumerUserService
      .registration(this.user)
      .toPromise()
      .then((consumerReg) => {
        this.logEvent('ecp.ui.heavy-reg', { eventState: 'end', correlationId: this.eventPairId });
        this.oidHeavyReg = false;

        this.loginService.getWidgetContext('heavyReg', extraParam).then(async (res) => {
          this.widgetContext = res;
          if (this.isBrowser && !this.tokenService.isInitialized) {
            await this.tokenService.initialize(
              this.widgetContext.oid.apiBaseURL,
              this.xApiKeyService.createApiHeader(),
              this.widgetContext.oid.oidConfigName,
              true
            );
          }
          // register and then bind consents to user
          this.captureConsentOnUser(consumerReg.data);
        });
      })
      .catch((err) => {
        this.widgetContext.createNewFlow.deferredCreationState = 'fail';
        this.widgetContext = { ...this.widgetContext };
        if (err.status === 404) {
          // No logic needed. Error message is is jsonOverride.
        } else {
          // TODO: This is likely a 500 Error. Maybe user should be navigated away?
          this.setAlert('error', 'Sorry, an account cannot be created at this time');
        }
      });
  }

  /**
   * Adds eConsent verbiage to the widget context.
   * @param res The widget context data
   * @returns The widget context data with eConsent verbiage added
   */
  public addEConsentText(res: LoginWidgetContextData): LoginWidgetContextData {
    if (!res?.eConsentFlow?.enabled) {
      return res;
    }

    // if it is enabled - we want to show, but we
    // will use new transclusion instead of legacy nested
    res.eConsentFlow.enabled = false;

    const consentManagement = this.adminPortalFeaturesService.config?.consentManagement;
    if (!consentManagement?.consentLibrary) {
      this.logger.warn('Legal consents not found in admin portal features', { component: 'LoginComponent' });
      return res;
    }
    this.consentLibrary = consentManagement.consentLibrary;
    this.consentsValid = false;
    if (consentManagement.primaryAcknowledgement) {
      this.primaryAck = {
        ...consentManagement.primaryAcknowledgement,
        isAccepted: false,
        required: true
      };
    } else {
      this.primaryAck = null;
    }
    this.additionalAck = (consentManagement.additionalAcknowledgements ?? []).map((ack) => ({ ...ack }));
    return res;
  }

  /**
   * Verifies if all required consents are accepted.
   * @param consent The consent that was clicked
   */
  public captureConsent(ack: IEconsentAcknowledgment): void {
    ack.isAccepted = !ack.isAccepted;
    this.consentsValid = this.verifyConsents();
  }

  /**
   * Touches all consents if they are present and then
   * returns true if all valid otherwise false.
   * @returns True if valid, otherwise false.
   */
  private verifyConsents(): boolean {
    let result = true;
    if (this.consents && this.consents.length > 0) {
      for (const consent of this.consents) {
        if (consent.required && !consent.value) {
          result = false;
        }
      }
    }
    if (!result) {
      this.showSpinner = false;
    }
    return result;
  }

  /**
   * Only called after registration is completed,
   * this will associate accepted consents with the user's identity.
   */
  private captureConsentOnUser(consumerReg: ConsumerRegistration): void {
    let flow = this.isOaoFlow ? 'api' : null;
    if (flow === null) {
      flow = 'ola'; // we fall back on ola because we need headers to continue, and ola is where heavy reg is more common
      // since it isn't used in the API method for any change in logic, it is fine to force the object.
    }
    const consent = this.primaryAck;
    if (consent?.isAccepted) {
      this.applicationApiService.captureConsent(
        flow,
        {
          acceptenceId: crypto.randomUUID(),
          acknowledgementId: consent.acknowledgeId,
          isCoApplicant: false, // note this likely is a coapplicant, but they are registering and need consent associated with them
          email: consumerReg.email,
          mobile: consumerReg.cellPhone,
          lenderCode: consumerReg.lenderCode
        },
        false
      );
    }
    for (const ack of this.additionalAck) {
      if (ack.isAccepted) {
        this.applicationApiService.captureConsent(
          flow,
          {
            acceptenceId: crypto.randomUUID(),
            acknowledgementId: ack.acknowledgeId,
            isCoApplicant: false, // note this likely is a coapplicant, but they are registering and need consent associated with them
            email: consumerReg.email,
            mobile: consumerReg.cellPhone,
            lenderCode: consumerReg.lenderCode
          },
          false
        );
      }
    }
  }

  /**
   * Print Consent
   */
  public print(): void {
    this.globalService.print(this.econsentContent.description, RequestHeaderConst.contentType);
  }
}
